import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, DollarSign, PieChart, Activity } from "lucide-react";
import { HoldingWithInstrumentAndAccount, TransactionWithInstrumentAndAccount } from "@/types";

interface ReportDataSummaryProps {
  holdings: HoldingWithInstrumentAndAccount[];
  transactions: TransactionWithInstrumentAndAccount[];
  isLoadingData: boolean;
}

export const ReportDataSummary: React.FC<ReportDataSummaryProps> = ({
  holdings,
  transactions,
  isLoadingData,
}) => {
  const confirmedHoldings = holdings.filter(h => h.isConfirmed);
  const totalValue = holdings.reduce((sum, holding) => sum + holding.baseAmount, 0);
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoadingData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BarChart3 className="h-4 w-4 mr-2" />
            Loading Data...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <BarChart3 className="h-4 w-4 mr-2" />
          Available Data Summary
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <PieChart className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Holdings</p>
              <p className="font-semibold">{holdings.length} positions</p>
              <div className="flex gap-1 mt-1">
                <Badge variant="outline" className="text-xs">
                  {confirmedHoldings.length} confirmed
                </Badge>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <DollarSign className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total Value</p>
              <p className="font-semibold">{formatCurrency(totalValue)}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Activity className="h-8 w-8 text-orange-500" />
            <div>
              <p className="text-sm text-muted-foreground">Transactions</p>
              <p className="font-semibold">{transactions.length} records</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 